"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2, Send, ExternalLink, Settings, AlertCircle, CheckCircle, Plus } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
    CANONICAL_MEETING_VARIABLE_SPECS,
    MEETING_AUTOMATION_TEMPLATE_PRESETS,
    type MeetingAutomationTemplatePresetId,
} from "@/pages/meetingAutomationTemplateUtils"
import { whatsappService } from "@/services/whatsappService"
import type { WhatsAppTemplate, TemplateComponents, WABA } from "@/services/whatsappService"
import { estiloDeCanal } from '@/lib/colores-de-canal'

interface WhatsAppTemplateSelectorProps {
    userId: string
    onTemplateSent?: () => void
}

interface VariableValue {
    index: number
    value: string
    placeholder: string
}

const PLACEHOLDER_REGEX = /\{\{(\d+)\}\}/g

function applyMeetingPresetToDraft(
    current: {
        name: string
        language: string
        category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'
        bodyText: string
        exampleBodyText: string
    },
    presetId: MeetingAutomationTemplatePresetId,
) {
    const preset = MEETING_AUTOMATION_TEMPLATE_PRESETS[presetId]
    return {
        ...current,
        name: current.name.trim() ? current.name : preset.suggestedName,
        language: 'es_ES',
        category: 'UTILITY' as const,
        bodyText: preset.bodyText,
        exampleBodyText: preset.exampleValuesLine,
    }
}

function hasPlaceholders(value: unknown): boolean {
    if (typeof value === 'string') {
        return /\{\{\d+\}\}/.test(value)
    }
    if (Array.isArray(value)) {
        return value.some(hasPlaceholders)
    }
    if (value && typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).some(hasPlaceholders)
    }
    return false
}

function getUnsupportedTemplateReason(template: WhatsAppTemplate): string | null {
    const unsupportedParameterizedComponents = template.components
        .filter((component) => {
            const type = String(component.type || '').toUpperCase()
            return type !== 'BODY' && hasPlaceholders(component)
        })
        .map((component) => String(component.type || 'UNKNOWN').toUpperCase())

    if (unsupportedParameterizedComponents.length === 0) {
        return null
    }

    const uniqueTypes = Array.from(new Set(unsupportedParameterizedComponents)).join(', ')
    return `Esta plantilla requiere campos parametrizados en ${uniqueTypes}. Aún no están soportados en este popup.`
}

// Parse template body text to extract variable placeholders like {{1}}, {{2}}
function extractVariables(template: WhatsAppTemplate): VariableValue[] {
    const variables: VariableValue[] = []
    const bodyComponent = template.components.find(c => c.type === 'BODY')

    if (bodyComponent?.text) {
        let match
        const seenIndices = new Set<number>()
        PLACEHOLDER_REGEX.lastIndex = 0

        while ((match = PLACEHOLDER_REGEX.exec(bodyComponent.text)) !== null) {
            const index = parseInt(match[1], 10)
            if (!seenIndices.has(index)) {
                seenIndices.add(index)

                // Try to get example value from template
                const example = bodyComponent.example?.body_text?.[0]?.[index - 1] || ''

                variables.push({
                    index,
                    value: '',
                    placeholder: example || `Variable ${index}`
                })
            }
        }

        // Sort by index
        variables.sort((a, b) => a.index - b.index)
    }

    return variables
}

// Preview template text with filled variables
function previewTemplate(template: WhatsAppTemplate, variables: VariableValue[]): string {
    const bodyComponent = template.components.find(c => c.type === 'BODY')
    if (!bodyComponent?.text) return ''

    let preview = bodyComponent.text
    variables.forEach(v => {
        const displayValue = v.value || `[${v.placeholder}]`
        preview = preview.replace(new RegExp(`\\{\\{${v.index}\\}\\}`, 'g'), displayValue)
    })

    return preview
}

export function WhatsAppTemplateSelector({ userId, onTemplateSent }: WhatsAppTemplateSelectorProps) {
    const { t } = useTranslation()
    const { lang } = useParams<{ lang: string }>()

    const [wabas, setWabas] = useState<WABA[]>([])
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])
    const [loadingWabas, setLoadingWabas] = useState(true)
    const [loadingTemplates, setLoadingTemplates] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [noWhatsAppConnected, setNoWhatsAppConnected] = useState(false)

    // Dialog state
    const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [variables, setVariables] = useState<VariableValue[]>([])
    const [sending, setSending] = useState(false)
    const [sendSuccess, setSendSuccess] = useState(false)
    const [sendError, setSendError] = useState<string | null>(null)
    const [unsupportedTemplateReason, setUnsupportedTemplateReason] = useState<string | null>(null)

    // Create template dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState<string | null>(null)
    const [createSuccess, setCreateSuccess] = useState(false)
    const [createFormData, setCreateFormData] = useState({
        name: '',
        language: 'es_ES',
        category: 'UTILITY' as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
        bodyText: '',
        exampleBodyText: ''
    })

    // Fetch WABAs on mount
    useEffect(() => {
        const fetchWabas = async () => {
            setLoadingWabas(true)
            setError(null)
            setNoWhatsAppConnected(false)

            try {
                const wabaList = await whatsappService.getWabas()

                if (wabaList.length === 0) {
                    setNoWhatsAppConnected(true)
                    return
                }

                setWabas(wabaList)

                // Auto-fetch templates for first WABA
                if (wabaList.length > 0) {
                    await fetchTemplates(wabaList[0].id)
                }
            } catch (err) {
                console.error('Error fetching WABAs:', err)
                // Check if this is an auth/connection error
                if (err instanceof Error && (err.message.includes('400') || err.message.includes('token'))) {
                    setNoWhatsAppConnected(true)
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to load WhatsApp accounts')
                }
            } finally {
                setLoadingWabas(false)
            }
        }

        fetchWabas()
    }, [])

    const fetchTemplates = async (wabaId: string) => {
        setLoadingTemplates(true)
        try {
            const templateList = await whatsappService.getTemplates(wabaId)
            setTemplates(templateList)
        } catch (err) {
            console.error('Error fetching templates:', err)
            setError(err instanceof Error ? err.message : 'Failed to load templates')
        } finally {
            setLoadingTemplates(false)
        }
    }

    const handleOpenDialog = (template: WhatsAppTemplate) => {
        setSelectedTemplate(template)
        setVariables(extractVariables(template))
        setUnsupportedTemplateReason(getUnsupportedTemplateReason(template))
        setSendSuccess(false)
        setSendError(null)
        setDialogOpen(true)
    }

    const handleVariableChange = (index: number, value: string) => {
        setVariables(prev =>
            prev.map(v => v.index === index ? { ...v, value } : v)
        )
    }

    const handleSend = async () => {
        if (!selectedTemplate) return

        if (unsupportedTemplateReason) {
            setSendError(unsupportedTemplateReason)
            return
        }

        const missingRequiredVariables = variables.filter(v => !v.value.trim())
        if (missingRequiredVariables.length > 0) {
            setSendError('Completa todos los campos de la plantilla antes de enviar.')
            return
        }

        setSending(true)
        setSendError(null)

        try {
            // Build components with body parameters
            const components: TemplateComponents = {}

            if (variables.length > 0) {
                components.body = variables.map(v => ({
                    type: 'text' as const,
                    text: v.value.trim()
                }))
            }

            // Get the template body text for display in the conversation
            const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY')
            const templateBodyText = bodyComponent?.text ? previewTemplate(selectedTemplate, variables) : undefined

            const result = await whatsappService.sendTemplateToLead(
                userId,
                selectedTemplate.name,
                selectedTemplate.language,
                Object.keys(components).length > 0 ? components : undefined,
                templateBodyText
            )

            if (result.success) {
                setSendSuccess(true)
                onTemplateSent?.()

                // Close dialog after short delay
                setTimeout(() => {
                    setDialogOpen(false)
                    setSendSuccess(false)
                }, 1500)
            } else {
                setSendError(result.error || 'Failed to send template')
            }
        } catch (err) {
            console.error('Error sending template:', err)
            setSendError(err instanceof Error ? err.message : 'Failed to send template')
        } finally {
            setSending(false)
        }
    }

    const hasMissingRequiredVariables = variables.some(v => !v.value.trim())

    const handleApplyMeetingPreset = (presetId: MeetingAutomationTemplatePresetId) => {
        setCreateFormData((current) => applyMeetingPresetToDraft(current, presetId))
    }

    const handleCreateTemplate = async () => {
        if (wabas.length === 0) {
            setCreateError('No WhatsApp Business Account available')
            return
        }

        const wabaId = wabas[0].id

        // Validation
        if (!createFormData.name.trim()) {
            setCreateError(t('user_profile.template_name_required') || 'Template name is required')
            return
        }
        if (!createFormData.bodyText.trim()) {
            setCreateError(t('user_profile.body_text_required') || 'Body text is required')
            return
        }

        setCreating(true)
        setCreateError(null)
        setCreateSuccess(false)

        try {
            // Parse example body text if provided
            let exampleBodyText: string[][] | undefined
            if (createFormData.exampleBodyText.trim()) {
                const lines = createFormData.exampleBodyText.trim().split('\n').filter(l => l.trim())
                exampleBodyText = lines.map(line => {
                    const values = line.includes(',') 
                        ? line.split(',').map(v => v.trim()).filter(v => v)
                        : line.split(/\s+/).filter(v => v)
                    return values
                })
            }

            await whatsappService.createTemplate(
                wabaId,
                createFormData.name.trim(),
                createFormData.language,
                createFormData.category,
                createFormData.bodyText.trim(),
                exampleBodyText
            )

            setCreateSuccess(true)
            
            // Reset form
            setCreateFormData({
                name: '',
                language: 'es_ES',
                category: 'UTILITY',
                bodyText: '',
                exampleBodyText: ''
            })

            // Refresh templates list
            await fetchTemplates(wabaId)

            // Close dialog after short delay
            setTimeout(() => {
                setCreateDialogOpen(false)
                setCreateSuccess(false)
            }, 1500)
        } catch (err) {
            console.error('Error creating template:', err)
            setCreateError(err instanceof Error ? err.message : 'Failed to create template')
        } finally {
            setCreating(false)
        }
    }

    // Status badge styling
    const getStatusBadge = (status: string) => {
        const isApproved = status === 'APPROVED'
        return (
            <Badge variant={isApproved ? "default" : "secondary"} className={isApproved ? "bg-success text-success-foreground" : ""}>
                {isApproved ? t('user_profile.status_approved') || 'Activada' : t('user_profile.status_pending') || 'Pendiente'}
            </Badge>
        )
    }

    // No WhatsApp connected state
    if (noWhatsAppConnected) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" style={estiloDeCanal('whatsapp')}>
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        {t('user_profile.whatsapp_templates') || 'Plantillas WhatsApp'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert className="border-warning/50 bg-warning/10">
                        <AlertCircle className="h-4 w-4 text-warning" />
                        <AlertDescription className="flex items-center justify-between">
                            <span>{t('user_profile.no_whatsapp') || 'WhatsApp no conectado'}</span>
                            <Button variant="outline" size="sm" asChild>
                                <Link to={`/${lang}/settings`} className="flex items-center gap-2">
                                    <Settings className="h-4 w-4" />
                                    {t('user_profile.connect_settings') || 'Conectar en Ajustes'}
                                </Link>
                            </Button>
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" style={estiloDeCanal('whatsapp')}>
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        {t('user_profile.whatsapp_templates') || 'Plantillas WhatsApp'}
                    </span>
                    {loadingWabas && <Loader2 className="h-4 w-4 animate-spin" />}
                </CardTitle>
                <CardDescription className="flex items-center justify-between">
                    <span>{t('user_profile.whatsapp_templates_description') || 'Envía un mensaje de plantilla a este contacto'}</span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setCreateDialogOpen(true)
                                setCreateError(null)
                                setCreateSuccess(false)
                            }}
                            disabled={loadingWabas || wabas.length === 0}
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            {t('user_profile.create_template') || 'Crear Plantilla'}
                        </Button>
                        <Button variant="ghost" size="xs" asChild>
                            <a
                                href="https://business.facebook.com/latest/whatsapp_manager/message_templates"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1"
                            >
                                <ExternalLink className="h-3 w-3" />
                                {t('user_profile.manage_in_meta') || 'Gestionar en Meta'}
                            </a>
                        </Button>
                    </div>
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('user_profile.template_name') || 'Nombre'}</TableHead>
                                <TableHead>{t('user_profile.template_status') || 'Estado'}</TableHead>
                                <TableHead className="text-right">{t('user_profile.action') || 'Acción'}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loadingTemplates ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-4">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : templates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-4 text-muted-foreground">
                                        {t('user_profile.no_templates') || 'No hay plantillas disponibles'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                templates.map((template) => (
                                    <TableRow key={`${template.name}-${template.language}`}>
                                        <TableCell className="font-medium">{template.name}</TableCell>
                                        <TableCell>{getStatusBadge(template.status)}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={template.status !== 'APPROVED'}
                                                onClick={() => handleOpenDialog(template)}
                                                className="gap-1"
                                            >
                                                <Send className="h-3 w-3" />
                                                {t('user_profile.send_template') || 'Enviar'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Variable Input Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Send className="h-4 w-4" />
                                {selectedTemplate?.name}
                            </DialogTitle>
                            <DialogDescription>
                                {unsupportedTemplateReason
                                    ? unsupportedTemplateReason
                                    : variables.length > 0
                                    ? (t('user_profile.template_variables') || 'Completa las variables de la plantilla')
                                    : (t('user_profile.confirm_send') || 'Confirmar envío de plantilla')
                                }
                            </DialogDescription>
                        </DialogHeader>

                        {sendSuccess ? (
                            <div className="flex flex-col items-center py-6 text-success">
                                <CheckCircle className="h-12 w-12 mb-2" />
                                <p className="font-medium">{t('user_profile.send_success') || '¡Plantilla enviada con éxito!'}</p>
                            </div>
                        ) : (
                            <>
                                {variables.length > 0 && (
                                    <div className="space-y-4 py-4">
                                        {variables.map((v) => (
                                            <div key={v.index} className="space-y-2">
                                                <Label htmlFor={`var-${v.index}`}>
                                                    {t('user_profile.variable') || 'Variable'} {v.index} *
                                                </Label>
                                                <Input
                                                    id={`var-${v.index}`}
                                                    placeholder={v.placeholder}
                                                    value={v.value}
                                                    onChange={(e) => handleVariableChange(v.index, e.target.value)}
                                                    required
                                                />
                                            </div>
                                        ))}

                                        {/* Preview */}
                                        {selectedTemplate && (
                                            <div className="mt-4 p-3 bg-muted rounded-md">
                                                <p className="text-xs text-muted-foreground mb-1">
                                                    {t('user_profile.preview') || 'Vista previa'}:
                                                </p>
                                                <p className="text-sm whitespace-pre-wrap">
                                                    {previewTemplate(selectedTemplate, variables)}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {sendError && (
                                    <Alert variant="destructive" className="mt-2">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{sendError}</AlertDescription>
                                    </Alert>
                                )}

                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={sending}>
                                        {t('common.cancel') || 'Cancelar'}
                                    </Button>
                                    <Button
                                        onClick={handleSend}
                                        disabled={sending || !!unsupportedTemplateReason || hasMissingRequiredVariables}
                                    >
                                        {sending ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                {t('user_profile.sending') || 'Enviando...'}
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                {t('user_profile.send') || 'Enviar'}
                                            </>
                                        )}
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Create Template Dialog */}
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                            <DialogTitle>
                                {t('user_profile.create_template') || 'Crear Plantilla WhatsApp'}
                            </DialogTitle>
                            <DialogDescription>
                                {t('user_profile.create_template_description') || 
                                'Crea una nueva plantilla de mensaje WhatsApp.'}
                            </DialogDescription>
                        </DialogHeader>

                        {createSuccess ? (
                            <div className="flex flex-col items-center py-6 text-success">
                                <CheckCircle className="h-12 w-12 mb-2" />
                                <p className="font-medium">
                                    {t('user_profile.create_template_success') || '¡Plantilla creada con éxito!'}
                                </p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    {t('user_profile.create_template_success_note') || 
                                    'La plantilla está pendiente de aprobación por Meta.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="create-template-name">
                                        {t('user_profile.template_name') || 'Nombre de Plantilla'} *
                                    </Label>
                                    <Input
                                        id="create-template-name"
                                        placeholder="mensaje_bienvenida"
                                        value={createFormData.name}
                                        onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                                        disabled={creating}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('user_profile.template_name_hint') || 
                                        'Solo letras, números, guiones bajos y guiones'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="create-template-language">
                                            {t('user_profile.language') || 'Idioma'} *
                                        </Label>
                                        <Select
                                            value={createFormData.language}
                                            onValueChange={(value) => setCreateFormData({ ...createFormData, language: value })}
                                            disabled={creating}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="es_ES">Español (es_ES)</SelectItem>
                                                <SelectItem value="en_US">English (en_US)</SelectItem>
                                                <SelectItem value="fr_FR">Français (fr_FR)</SelectItem>
                                                <SelectItem value="de_DE">Deutsch (de_DE)</SelectItem>
                                                <SelectItem value="it_IT">Italiano (it_IT)</SelectItem>
                                                <SelectItem value="pt_PT">Português (pt_PT)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="create-template-category">
                                            {t('user_profile.category') || 'Categoría'} *
                                        </Label>
                                        <Select
                                            value={createFormData.category}
                                            onValueChange={(value: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') => 
                                                setCreateFormData({ ...createFormData, category: value })}
                                            disabled={creating}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="UTILITY">
                                                    {t('user_profile.category_utility') || 'Utilidad'}
                                                </SelectItem>
                                                <SelectItem value="MARKETING">
                                                    {t('user_profile.category_marketing') || 'Marketing'}
                                                </SelectItem>
                                                <SelectItem value="AUTHENTICATION">
                                                    {t('user_profile.category_authentication') || 'Autenticación'}
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="create-template-body">
                                        {t('user_profile.body_text') || 'Texto del Cuerpo'} *
                                    </Label>
                                    <Textarea
                                        id="create-template-body"
                                        placeholder="Hola, ¡bienvenido a nuestro servicio!"
                                        value={createFormData.bodyText}
                                        onChange={(e) => setCreateFormData({ ...createFormData, bodyText: e.target.value })}
                                        disabled={creating}
                                        rows={4}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('user_profile.body_text_hint') || 'Usa {{1}}, {{2}}, etc. para variables. Máximo 1024 caracteres.'}
                                    </p>
                                </div>

                                <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-medium">
                                                {t('user_profile.meeting_automation_contract_title', {
                                                    defaultValue: 'Contrato de variables para automatización de visitas',
                                                })}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('user_profile.meeting_automation_contract_description', {
                                                    defaultValue:
                                                        'Si esta plantilla se va a usar en la automatización pre/post visita, los placeholders deben seguir exactamente este orden semántico.',
                                                })}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleApplyMeetingPreset('pre_visit')}
                                                disabled={creating}
                                            >
                                                {t('user_profile.apply_pre_visit_preset', {
                                                    defaultValue: 'Usar preset de previsita',
                                                })}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleApplyMeetingPreset('post_visit')}
                                                disabled={creating}
                                            >
                                                {t('user_profile.apply_post_visit_preset', {
                                                    defaultValue: 'Usar preset de postvisita',
                                                })}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        {CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => (
                                            <div key={spec.slot} className="rounded-md border bg-background px-3 py-2 text-xs">
                                                <div className="font-medium">{spec.slot} {spec.key}</div>
                                                <div className="text-muted-foreground">{spec.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="create-template-example-values">
                                        {t('user_profile.example_values') || 'Valores de Ejemplo (Opcional)'}
                                    </Label>
                                    <Textarea
                                        id="create-template-example-values"
                                        placeholder={CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => spec.example).join(', ')}
                                        value={createFormData.exampleBodyText}
                                        onChange={(e) => setCreateFormData({ ...createFormData, exampleBodyText: e.target.value })}
                                        disabled={creating}
                                        rows={3}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('user_profile.example_hint') ||
                                            'Una fila de ejemplo por línea. Los valores deben respetar el orden de {{1}}, {{2}}, etc.'}
                                    </p>
                                </div>

                                {createError && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{createError}</AlertDescription>
                                    </Alert>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setCreateDialogOpen(false)}
                                disabled={creating || createSuccess}
                            >
                                {createSuccess 
                                    ? (t('common.close') || 'Cerrar')
                                    : (t('common.cancel') || 'Cancelar')
                                }
                            </Button>
                            {!createSuccess && (
                                <Button onClick={handleCreateTemplate} disabled={creating}>
                                    {creating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('user_profile.creating') || 'Creando...'}
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="h-4 w-4 mr-2" />
                                            {t('user_profile.create') || 'Crear'}
                                        </>
                                    )}
                                </Button>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    )
}
